import { useEffect, useRef } from 'react';
import { LogoutOptions, LogoutReason } from '../../../shared/auth';
import { ACTIVITY_EVENTS, ACTIVITY_PUBLISH_THROTTLE_MS } from './constants';
import {
  getForceLogoutStorageKey,
  getLastActivityStorageKey,
  getOrCreateTabId,
  openInactivityChannel,
  publishSharedActivity,
  publishSharedForceLogout,
  readStoredForceLogout,
  readStoredLastActivity,
  SharedActivityPayloadSchema,
  SharedLogoutPayloadSchema,
  type SharedActivityPayload,
  type SharedLogoutPayload,
} from './crossTab';

type UseInactivityLogoutParams = {
  isEnabled: boolean;
  userId: string | null;
  sessionKey: string | null;
  inactivityLimitMs: number | null;
  logout: (options?: LogoutOptions) => Promise<void>;
};

export function useInactivityLogout({
  isEnabled,
  userId,
  sessionKey,
  inactivityLimitMs,
  logout,
}: UseInactivityLogoutParams): void {
  const tabIdRef = useRef<string | null>(null);
  const lastActivityAtRef = useRef<number>(Date.now());
  const lastPublishedActivityAtRef = useRef<number>(0);
  const logoutTimeoutIdRef = useRef<number | null>(null);
  const isLoggingOutRef = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const isTimerEnabled =
      isEnabled &&
      Boolean(userId) &&
      typeof inactivityLimitMs === 'number' &&
      Number.isFinite(inactivityLimitMs) &&
      inactivityLimitMs > 0;

    if (!isTimerEnabled || !userId || inactivityLimitMs === null) {
      return;
    }

    const crossTabEnabled = Boolean(sessionKey);
    const tabId = getOrCreateTabId();
    tabIdRef.current = tabId;
    isLoggingOutRef.current = false;
    lastPublishedActivityAtRef.current = 0;
    channelRef.current = crossTabEnabled ? openInactivityChannel() : null;

    const clearScheduledLogout = () => {
      if (logoutTimeoutIdRef.current !== null) {
        window.clearTimeout(logoutTimeoutIdRef.current);
        logoutTimeoutIdRef.current = null;
      }
    };

    const triggerLogout = async (reason: LogoutReason.TIMEOUT | LogoutReason.REMOTE) => {
      if (isLoggingOutRef.current) {
        return;
      }

      isLoggingOutRef.current = true;
      clearScheduledLogout();
      await logout({ reason, broadcast: false });
    };

    const scheduleLogoutCheck = () => {
      clearScheduledLogout();

      const remainingMs =
        inactivityLimitMs - (Date.now() - lastActivityAtRef.current);

      if (remainingMs <= 0) {
        void triggerTimeoutLogout();
        return;
      }

      logoutTimeoutIdRef.current = window.setTimeout(() => {
        void triggerTimeoutLogout();
      }, remainingMs);
    };

    const applySharedActivity = (
      payload: SharedActivityPayload,
      ignoreOwnSource: boolean
    ) => {
      if (!crossTabEnabled || !sessionKey) {
        return;
      }

      if (payload.sessionKey !== sessionKey || payload.userId !== userId) {
        return;
      }

      if (ignoreOwnSource && payload.sourceTabId === tabIdRef.current) {
        return;
      }

      if (payload.timestamp <= lastActivityAtRef.current) {
        return;
      }

      lastActivityAtRef.current = payload.timestamp;
      scheduleLogoutCheck();
    };

    const applySharedLogout = async (
      payload: SharedLogoutPayload,
      ignoreOwnSource: boolean
    ) => {
      if (!crossTabEnabled || !sessionKey) {
        return;
      }

      if (payload.sessionKey !== sessionKey || payload.userId !== userId) {
        return;
      }

      if (ignoreOwnSource && payload.sourceTabId === tabIdRef.current) {
        return;
      }

      await triggerLogout(LogoutReason.REMOTE);
    };

    const publishLocalActivity = (timestamp: number) => {
      if (!crossTabEnabled || !sessionKey) {
        return;
      }

      if (
        timestamp - lastPublishedActivityAtRef.current <
        ACTIVITY_PUBLISH_THROTTLE_MS
      ) {
        return;
      }

      lastPublishedActivityAtRef.current = timestamp;
      publishSharedActivity(
        sessionKey,
        {
          type: 'activity',
          sessionKey,
          userId,
          inactivityLimitMs,
          timestamp,
          sourceTabId: tabId,
        },
        channelRef.current
      );
    };

    const triggerTimeoutLogout = async () => {
      if (isLoggingOutRef.current) {
        return;
      }

      isLoggingOutRef.current = true;
      clearScheduledLogout();

      if (crossTabEnabled && sessionKey) {
        publishSharedForceLogout(
          sessionKey,
          {
            type: 'forceLogout',
            sessionKey,
            userId,
            timestamp: Date.now(),
            reason: LogoutReason.TIMEOUT,
            sourceTabId: tabId,
          },
          channelRef.current
        );
      }

      await logout({ reason: LogoutReason.TIMEOUT, broadcast: false });
    };

    const revalidateFromSharedState = () => {
      if (!crossTabEnabled || !sessionKey || isLoggingOutRef.current) {
        scheduleLogoutCheck();
        return;
      }

      const storedLogout = readStoredForceLogout(sessionKey);
      if (storedLogout) {
        void applySharedLogout(storedLogout, false);
        return;
      }

      const storedActivity = readStoredLastActivity(sessionKey);
      if (storedActivity) {
        applySharedActivity(storedActivity, false);
        return;
      }

      scheduleLogoutCheck();
    };

    const handleLocalActivity = () => {
      if (isLoggingOutRef.current) {
        return;
      }

      const now = Date.now();
      lastActivityAtRef.current = now;
      scheduleLogoutCheck();
      publishLocalActivity(now);
    };

    const handleStorageEvent = (event: StorageEvent) => {
      if (!crossTabEnabled || !sessionKey || !event.newValue) {
        return;
      }

      const isActivityEvent =
        event.key === getLastActivityStorageKey(sessionKey);
      const isLogoutEvent =
        event.key === getForceLogoutStorageKey(sessionKey);

      if (!isActivityEvent && !isLogoutEvent) {
        return;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(event.newValue);
      } catch {
        return;
      }

      if (isActivityEvent) {
        const result = SharedActivityPayloadSchema.safeParse(parsed);
        if (result.success) {
          applySharedActivity(result.data, true);
        }
      }

      if (isLogoutEvent) {
        const result = SharedLogoutPayloadSchema.safeParse(parsed);
        if (result.success) {
          void applySharedLogout(result.data, true);
        }
      }
    };

    const handleBroadcastMessage = (event: MessageEvent<unknown>) => {
      const payload = event.data;

      const activityResult = SharedActivityPayloadSchema.safeParse(payload);
      if (activityResult.success) {
        applySharedActivity(activityResult.data, true);
        return;
      }

      const logoutResult = SharedLogoutPayloadSchema.safeParse(payload);
      if (logoutResult.success) {
        void applySharedLogout(logoutResult.data, true);
      }
    };

    const handleFocusRevalidation = () => {
      if (!isLoggingOutRef.current) {
        revalidateFromSharedState();
      }
    };

    const handleVisibilityRevalidation = () => {
      if (
        document.visibilityState === 'visible' &&
        !isLoggingOutRef.current
      ) {
        revalidateFromSharedState();
      }
    };

    const storedActivity =
      crossTabEnabled && sessionKey ? readStoredLastActivity(sessionKey) : null;
    lastActivityAtRef.current = storedActivity?.timestamp ?? Date.now();

    if (!storedActivity && crossTabEnabled && sessionKey) {
      publishLocalActivity(lastActivityAtRef.current);
    }

    if (crossTabEnabled && sessionKey) {
      const storedLogout = readStoredForceLogout(sessionKey);
      if (storedLogout) {
        void applySharedLogout(storedLogout, false);
      }
    }

    scheduleLogoutCheck();

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, handleLocalActivity, {
        passive: true,
      });
    }

    window.addEventListener('focus', handleFocusRevalidation);
    document.addEventListener('visibilitychange', handleVisibilityRevalidation);

    if (crossTabEnabled) {
      window.addEventListener('storage', handleStorageEvent);
      channelRef.current?.addEventListener('message', handleBroadcastMessage);
    }

    return () => {
      clearScheduledLogout();

      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, handleLocalActivity);
      }

      window.removeEventListener('focus', handleFocusRevalidation);
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityRevalidation
      );

      if (crossTabEnabled) {
        window.removeEventListener('storage', handleStorageEvent);
        channelRef.current?.removeEventListener(
          'message',
          handleBroadcastMessage
        );
      }

      channelRef.current?.close();
      channelRef.current = null;
      isLoggingOutRef.current = false;
    };
  }, [isEnabled, userId, sessionKey, inactivityLimitMs, logout]);
}
